import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import { faBan, faCheckCircle } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import { getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'

import PageContext from '@contexts/pageContext'
import type { DefaultContext } from '@contexts/pageContext'

import getActiveTabBar from '@functions/tabBar'

import useProductOptions from '@hooks/useProductOptions'
import { generateUrls } from '@hooks/useProductOption'

import ListBlankState from '@components/ListBlankState'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import ProductOptionListItem from '../ProductOptionListItem'

import type { PageContextPayload } from '../../index'

const buildPageFilterFields = () => {
  const style = { marginBottom: 16 }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        placeholder: 'Search by product option name or ID',
        style,
      },
      key: 'string',
      label: 'Name or ID',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'All',
        inputComponent: 'select',
        style,
      },
      key: 'displayType',
      label: 'Display Type',
      values: [
        { key: 'Dropdown', label: 'Dropdown' },
        { key: 'MultiSelect', label: 'Multi Select' },
      ],
    },
  ]
}

interface PageContentProps {
  pageFilters: {},
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  // useLocation to refresh the window.location. If not used location won't reload if URL changes
  useLocation()

  const {
    isArchived: archived,
  } = parsePermittedQueryParams(getQueryParamsFromHash(), ['isArchived'])

  const isArchived = archived === 'true'

  const productOptionsPayload = useProductOptions({
    filters: {
      ...pageFilters,
      isArchived,
    },
    performHttpRequests: true,
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredProductOptions,
    filteredProductOptionsCount,
    hasFilteredProductOptions,
    loading,
  } = productOptionsPayload

  const pageContext = useContext<DefaultContext<PageContextPayload>>(PageContext)
  const {
    callbacks: {
      resetFilters,
      showCreateProductOptionModal,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const {
    archivedProductOptionsIndexUrl,
    productOptionsIndexUrl,
  } = generateUrls()

  return (
    <>
      <Helmet>
        <title>Product Options | AIM Digital</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button buttonStyle="primaryCreate" onClick={() => showCreateProductOptionModal()} size="medium">
            Create
          </Button>
        )}
        activeTabBarItemKey={getActiveTabBar({ isArchived })}
        tabBarItems={[
          {
            href: productOptionsIndexUrl,
            icon: faCheckCircle,
            key: 'active',
            title: 'Active',
          },
          {
            href: archivedProductOptionsIndexUrl,
            icon: faBan,
            key: 'archived',
            title: 'Archived',
          },
        ]}
        title="Product Options"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredProductOptions && <ListBlankState />}

            {!loading && hasFilteredProductOptions && (
              <DashboardModule contentBoxProps={{ flexDirection: 'column' }} title="Product Options">
                <Box flexDirection="column">
                  {filteredProductOptions.map(productOption => (
                    <ProductOptionListItem
                      key={productOption.id}
                      productOption={productOption}
                    />
                  ))}
                </Box>

                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredProductOptionsCount}
                  loading={loading}
                />
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              filterFields={buildPageFilterFields()}
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
